import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {Router} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronDown,
  faCircleInfo,
  faGear,
  faHandPointer,
  faLayerGroup,
  faMinus,
  faPlus,
  faSearch,
  faThumbtackAngle,
  faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import {MyCheckBuilderState, parseRulesetAiConfig, SelectionMode} from '../my-checks-builder.model';
import {RulesetRule, RulesetService} from '../../../../services/ruleset.service';
import {Ruleset, RulesetType} from '../../../ruleManagement/models/rule-management.models';
import {BicInputComponent} from '@buildinginformationcloud/bic-ui-components';
import {PermissionService} from '../../../../services/permission/permission.service';

@Component({
  selector: 'my-checks-builder-rule-step',
  standalone: true,
  imports: [FormsModule, FaIconComponent, BicInputComponent, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './builder-rule-step.component.html',
})
export class BuilderRuleStepComponent implements OnInit {
  @Input() state!: MyCheckBuilderState;
  /**
   * Projekt des Checks. **`undefined` im Admin-Modus** (zentral bereitgestellte Kopie, projektlos) –
   * projektgebundene Daten (Dokumentklassen, Projektnutzer) werden dann nicht geladen und die
   * zugehörigen Felder ausgeblendet. Regelsets werden mit `undefined` von sich aus nur global geladen.
   */
  @Input() projectId?: number;
  /** Zeigt Pflichtfeld-Hinweise an (nach erstem Speicherversuch). */
  @Input() showErrors = false;
  @Output() stateChange = new EventEmitter<Partial<MyCheckBuilderState>>();
  faSearch = faSearch;
  faLayerGroup = faLayerGroup;
  faWandMagicSparkles = faWandMagicSparkles;
  faGear = faGear;
  faCheck = faCheck;
  faChevronDown = faChevronDown;
  faMinus = faMinus;
  faPlus = faPlus;
  faCircleInfo = faCircleInfo;
  faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  prebuiltRulesets: Ruleset[] = [];
  customRulesets: Ruleset[] = [];
  searchQuery = '';
  loading = true;

  /** Aufgeklappte Regelsets (per ID) */
  expandedRulesetIds = new Set<string>();
  /** Geladene Einzelregeln je Regelset-ID */
  rulesByRulesetId: Record<string, RulesetRule[]> = {};
  /** Ladezustand je Regelset-ID */
  rulesLoading: Record<string, boolean> = {};
  /** Regelsets, deren Einzelregeln für die Bewertung der gespeicherten Auswahl gebraucht werden */
  private preloadTargetIds: string[] = [];

  private rulesetService = inject(RulesetService);
  private router = inject(Router);
  private permissionService = inject(PermissionService);

  /**
   * Zentral bereitgestellte Sets sind ohne Regelwerk-Admin-Recht nicht öffenbar (nur auswählbar/
   * ausführbar). Der "Öffnen"-Link in dieser Auswahlliste muss dieselbe Sperre wie die
   * Prüfregelverwaltung respektieren.
   */
  canOpen(ruleset: Ruleset): boolean {
    return !ruleset.readOnly || this.permissionService.hasGlobalPrivilege('GLOBAL_CHECKRULE_ADMIN');
  }

  private static readonly TYPE_PATH_MAP: Record<string, string> = {KI: 'ki', HYBRID: 'hybrid', IDS: 'ids'};

  get filteredPrebuilt(): Ruleset[] {
    return this.filterBySearch(this.prebuiltRulesets);
  }

  get filteredCustom(): Ruleset[] {
    return this.filterBySearch(this.customRulesets);
  }

  /** Alle Regelsets (vordefiniert zuerst, dann eigene) in einer flachen Liste */
  get allFilteredRulesets(): Ruleset[] {
    return [...this.filteredPrebuilt, ...this.filteredCustom];
  }

  /** Beschriftung des Anzahl-Badges eines Sets (z. B. "12 Regeln" oder "Einzelregel") */
  getSetCountLabel(ruleset: Ruleset): string {
    if (ruleset.ruleCount === 0) return 'Keine Prüfregeln';
    return ruleset.ruleCount <= 1 ? 'Einzelregel' : `${ruleset.ruleCount} Regeln`;
  }

  /**
   * Basisroute der Regelwerksverwaltung.
   *
   * <p>Im Admin-Modus (projektlos) führen die Links in den globalen Regelwerk-Adminbereich – eine
   * `['projects', undefined, ...]`-Route würde sonst ins Leere zeigen.
   */
  private get ruleManagementBase(): any[] {
    return this.projectId
      ? ['projects', this.projectId, 'my-checks']
      : ['admin', 'my-checks'];
  }

  selectMode(mode: SelectionMode): void {
    this.stateChange.emit({ruleSelectionMode: mode});
  }

  // ─── Regelset-Auswahl (ganzes Set) ──────────────────────────────────────────

  /** Ein Regelset ist als Ganzes ausgewählt */
  isSelected(ruleset: Ruleset): boolean {
    return this.state.selectedRuleSetIds.includes(ruleset.id);
  }

  /** Einzelne Regeln dieses Sets sind ausgewählt, aber nicht das ganze Set */
  isPartiallySelected(ruleset: Ruleset): boolean {
    if (this.isSelected(ruleset)) return false;
    // Persistierter Teilauswahl-Zustand: bleibt auch ohne (lazy) geladene Einzelregeln erhalten,
    // z. B. nach Navigation zwischen den Builder-Schritten oder erneutem Öffnen des Checks.
    if (this.state.partiallySelectedRuleSetIds?.includes(ruleset.id)) return true;
    // Fallback für ältere gespeicherte Checks ohne partiallySelectedRuleSetIds
    const ruleIds = this.getSetRuleIds(ruleset.id);
    return ruleIds.some(id => this.state.selectedRuleIds.includes(id));
  }

  /** Set kann aufgeklappt werden, sobald es mindestens eine Regel enthält */
  isExpandable(ruleset: Ruleset): boolean {
    return ruleset.ruleCount >= 1;
  }


  toggleRuleset(ruleset: Ruleset): void {
    const setIds = [...this.state.selectedRuleSetIds];
    let ruleIds = [...this.state.selectedRuleIds];
    const idx = setIds.indexOf(ruleset.id);
    if (idx >= 0) {
      setIds.splice(idx, 1);
    } else {
      setIds.push(ruleset.id);
      // Etwaige Einzelauswahl dieses Sets entfernen – das ganze Set deckt sie ab
      const setRuleIds = this.getSetRuleIds(ruleset.id);
      ruleIds = ruleIds.filter(id => !setRuleIds.includes(id));
    }
    this.stateChange.emit({
      selectedRuleSetIds: setIds,
      selectedRuleIds: ruleIds,
      // Voll- wie Abwahl des ganzen Sets beendet eine etwaige Teilauswahl
      partiallySelectedRuleSetIds: (this.state.partiallySelectedRuleSetIds ?? []).filter(id => id !== ruleset.id),
      selectedRulesetType: this.resolveSelectedRulesetType(setIds, ruleIds, ruleset.type),
      selectedRulesetTypes: this.resolveSelectedRulesetTypes(setIds, ruleIds),
    });
    // For KI rulesets, pull the prompts from the ruleset so they become part of the definition
    if (ruleset.type === 'KI') {
      this.loadAiConfig(ruleset.id);
    } else {
      this.stateChange.emit({
        aiSystemPrompt: '', aiTemplatePrompt: '', aiModel: '',
        aiInputParameters: [], aiCheckParameters: [], aiOutputParameters: [], aiTags: [], aiContextFiles: [],
      });
    }
  }

  /**
   * Load the full set-level AI configuration from an AI ruleset's configJson into the builder state.
   *
   * Das Parsen selbst liegt in {@link parseRulesetAiConfig} – dieselbe Funktion nutzt der Direktstart
   * eines Regelsets (`RulesetService#prepareDirectRun`), damit beide Wege dasselbe Manifest erzeugen.
   */
  private loadAiConfig(rulesetId: string): void {
    this.rulesetService.getKiRuleset(rulesetId, this.projectId, this.projectId == null).subscribe({
      next: (set) => {
        const ai = parseRulesetAiConfig(set?.configJson);
        this.stateChange.emit({
          aiSystemPrompt: ai.systemPrompt,
          aiTemplatePrompt: ai.templatePrompt,
          aiModel: ai.model,
          aiInputParameters: ai.inputParameters,
          aiCheckParameters: ai.checkParameters,
          aiOutputParameters: ai.outputParameters,
          aiTags: ai.tags,
          aiContextFiles: ai.contextFiles,
        });
      },
      error: () => {
        // Leave config empty; backend runtime resolution acts as fallback.
      },
    });
  }

  // ─── Aufklappen & Einzelregeln laden ────────────────────────────────────────

  isExpanded(ruleset: Ruleset): boolean {
    return this.expandedRulesetIds.has(ruleset.id);
  }

  toggleExpand(ruleset: Ruleset): void {
    if (this.expandedRulesetIds.has(ruleset.id)) {
      this.expandedRulesetIds.delete(ruleset.id);
      return;
    }
    this.expandedRulesetIds.add(ruleset.id);
    this.loadRules(ruleset);
  }

  /** Lädt die Einzelregeln eines Regelsets einmalig nach. */
  private loadRules(ruleset: Ruleset): void {
    if (this.rulesByRulesetId[ruleset.id] !== undefined || this.rulesLoading[ruleset.id]) return;
    this.rulesLoading[ruleset.id] = true;
    this.rulesetService.getRulesetRules(ruleset, this.projectId).subscribe({
      next: (rules) => {
        this.rulesByRulesetId[ruleset.id] = rules;
        this.rulesLoading[ruleset.id] = false;
      },
      error: () => {
        this.rulesByRulesetId[ruleset.id] = [];
        this.rulesLoading[ruleset.id] = false;
      },
    });
  }

  /**
   * Lädt die Einzelregeln der Sets, aus denen laut State einzelne Regeln gewählt sind – ohne
   * Aufklappen wären deren IDs keinem Set zuzuordnen. Die Zusammenfassung müsste sie dann blind
   * zählen und zeigt Regeln an, die es gar nicht mehr gibt (z. B. weil die Regeln des Sets
   * zwischenzeitlich neu angelegt/umbenannt wurden und dabei neue IDs bekommen haben).
   */
  private preloadRulesForSelection(): void {
    if (this.state.selectedRuleIds.length === 0) return;
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const partialIds = this.state.partiallySelectedRuleSetIds ?? [];
    // IDS-Spezifikationen tragen ihre Regelset-ID im Präfix ("<rulesetId>::<specName>").
    const prefixMatches = all.filter(rs => this.state.selectedRuleIds.some(id => id.startsWith(`${rs.id}::`)));
    const targeted = all.filter(rs => partialIds.includes(rs.id) || prefixMatches.includes(rs));
    // Altstände ohne partiallySelectedRuleSetIds lassen keine Zuordnung zu – dann einmalig alle
    // Sets auflösen, damit die Auswahl überhaupt bewertet werden kann.
    const targets = targeted.length > 0 ? targeted : all;
    this.preloadTargetIds = targets.map(rs => rs.id);
    targets.forEach(rs => this.loadRules(rs));
  }

  /**
   * True, solange die für die Auswahl benötigten Einzelregeln noch nachgeladen werden. Solange
   * das läuft, ist weder die Anzahl noch „verwaist ja/nein" belastbar. Bewusst nicht an
   * `rulesLoading` gehängt, damit ein manuelles Aufklappen die Zusammenfassung nicht flackern lässt.
   */
  get rulesPreloadPending(): boolean {
    return this.preloadTargetIds.some(id => this.rulesByRulesetId[id] === undefined);
  }

  /** Einzelregeln eines Sets, alphabetisch nach Kürzel sortiert (Fallback: Name, z.B. bei IDS ohne Kürzel). */
  getRules(ruleset: Ruleset): RulesetRule[] {
    const rules = this.rulesByRulesetId[ruleset.id] ?? [];
    return [...rules].sort((a, b) =>
      (a.code ?? a.name).localeCompare(b.code ?? b.name, 'de', {numeric: true}));
  }

  // ─── Einzelregel-Auswahl ────────────────────────────────────────────────────

  isRuleSelected(ruleset: Ruleset, rule: RulesetRule): boolean {
    // Bei vollständig ausgewähltem Set gelten alle Regeln als ausgewählt
    return this.isSelected(ruleset) || this.state.selectedRuleIds.includes(rule.id);
  }

  toggleRule(ruleset: Ruleset, rule: RulesetRule): void {
    let setIds = [...this.state.selectedRuleSetIds];
    let ruleIds = [...this.state.selectedRuleIds];
    const allRuleIds = this.getSetRuleIds(ruleset.id);

    if (setIds.includes(ruleset.id)) {
      // Vollauswahl → Teilauswahl umwandeln: Set entfernen, alle Regeln außer dieser übernehmen
      setIds = setIds.filter(id => id !== ruleset.id);
      for (const rid of allRuleIds) {
        if (rid !== rule.id && !ruleIds.includes(rid)) ruleIds.push(rid);
      }
    } else {
      const idx = ruleIds.indexOf(rule.id);
      if (idx >= 0) {
        ruleIds.splice(idx, 1);
      } else {
        ruleIds.push(rule.id);
      }
      // Sind danach alle Regeln ausgewählt → zur Set-Vollauswahl hochstufen
      if (allRuleIds.length > 0 && allRuleIds.every(id => ruleIds.includes(id))) {
        ruleIds = ruleIds.filter(id => !allRuleIds.includes(id));
        setIds.push(ruleset.id);
      }
    }

    // Teilauswahl-Zustand des Sets nachführen: partial = nicht voll ausgewählt,
    // aber mindestens eine Einzelregel des Sets gewählt
    const isPartial = !setIds.includes(ruleset.id) && allRuleIds.some(id => ruleIds.includes(id));
    let partialIds = (this.state.partiallySelectedRuleSetIds ?? []).filter(id => id !== ruleset.id);
    if (isPartial) {
      partialIds = [...partialIds, ruleset.id];
    }

    this.stateChange.emit({
      selectedRuleSetIds: setIds,
      selectedRuleIds: ruleIds,
      partiallySelectedRuleSetIds: partialIds,
      selectedRulesetType: this.resolveSelectedRulesetType(setIds, ruleIds, ruleset.type),
      selectedRulesetTypes: this.resolveSelectedRulesetTypes(setIds, ruleIds),
    });
  }

  /**
   * Leitet den Prüfungstyp aus der tatsächlich resultierenden Auswahl ab (statt einfach den Typ
   * des zuletzt (ab)gewählten Regelsets zu übernehmen). Sonst bleibt z. B. nach dem Abwählen einer
   * probeweise angeklickten KI-Regel `selectedRulesetType: 'KI'` stehen, obwohl nur noch IDS-Regeln
   * ausgewählt sind – die im Manifest verdrahtete Ausführungskette würde dann nicht zur Auswahl passen.
   *
   * Reihenfolge der Auflösung:
   *  1. das gerade bediente Set (`preferred`), solange es Teil der Auswahl ist,
   *  2. sonst das erste noch ausgewählte Set. Achtung: `all` ist nach Typ sortiert (KI vor HYBRID
   *     vor IDS, siehe RulesetService.listRulesets) – ohne Schritt 1 gewinnt bei einer gemischten
   *     Auswahl immer KI und ein IDS-Check bekäme die KI-Kette verdrahtet.
   *  3. gar nichts mehr ausgewählt → Default 'IDS', NICHT der Typ des abgewählten Sets.
   */
  private resolveSelectedRulesetType(setIds: string[], ruleIds: string[], preferred: RulesetType): RulesetType {
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const isPartOfSelection = (rs: Ruleset) => setIds.includes(rs.id)
      || this.getSetRuleIds(rs.id).some(id => ruleIds.includes(id));
    const selected = all.find(rs => rs.type === preferred && isPartOfSelection(rs))
      ?? all.find(rs => isPartOfSelection(rs));
    if (selected) return selected.type;
    return setIds.length === 0 && ruleIds.length === 0 ? 'IDS' : preferred;
  }

  /**
   * Typ je beteiligtem Regelset (`rulesetId` → Typ).
   *
   * Der Einzelwert `selectedRulesetType` kann eine gemischte Auswahl nicht abbilden; erst diese
   * Zuordnung macht sie darstellbar. Sie entscheidet in `buildManifestFromState`, ob die
   * Dispatcher-Kette (`multi_check_executor`) oder eine typspezifische Kette entsteht – der
   * Builder-Model-Code ist bewusst dependency-frei und kann Regelset-Typen nicht selbst nachladen.
   */
  private resolveSelectedRulesetTypes(setIds: string[], ruleIds: string[]): Record<string, RulesetType> {
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const types: Record<string, RulesetType> = {};
    for (const ruleset of all) {
      const isSelected = setIds.includes(ruleset.id)
        || this.getSetRuleIds(ruleset.id).some(id => ruleIds.includes(id));
      if (isSelected) types[ruleset.id] = ruleset.type;
    }
    // Bereits bekannte Typen erhalten: Regelsets, deren Regeln noch nicht geladen sind, würden
    // oben nicht als ausgewählt erkannt und ihren Typ sonst verlieren.
    for (const [id, type] of Object.entries(this.state.selectedRulesetTypes ?? {})) {
      if (!types[id] && (setIds.includes(id) || (this.state.partiallySelectedRuleSetIds ?? []).includes(id))) {
        types[id] = type;
      }
    }
    return types;
  }

  /**
   * Gleicht den gespeicherten Prüfungstyp einmalig gegen die tatsächlich ausgewählten Regelsets ab.
   * `selectedRulesetType` bestimmt in buildManifestFromState die komplette Ausführungskette und wird
   * über `ruleTypes` wieder eingelesen – ein einmal falsch gesetzter Wert (z. B. 'KI' aus einem
   * probeweise gewählten Set) bleibt daher kleben und verdrahtet beim nächsten „Übernehmen" erneut
   * die KI-Kette in einen IDS-Check, ohne dass der Nutzer etwas an der Regelauswahl geändert hat.
   */
  private reconcileSelectedRulesetType(): void {
    if (this.resolvedSelectedSets.length === 0) return;
    const derived = this.resolveSelectedRulesetType(
      this.state.selectedRuleSetIds,
      this.state.selectedRuleIds,
      this.state.selectedRulesetType,
    );
    const derivedTypes = this.resolveSelectedRulesetTypes(
      this.state.selectedRuleSetIds,
      this.state.selectedRuleIds,
    );
    const patch: Partial<MyCheckBuilderState> = {};
    if (derived !== this.state.selectedRulesetType) {
      patch.selectedRulesetType = derived;
    }
    // Alt-Manifeste haben die Zuordnung noch nicht; beim Öffnen einmalig nachziehen, damit eine
    // gemischte Auswahl auch dort die Dispatcher-Kette bekommt.
    if (Object.keys(derivedTypes).length > 0
      && JSON.stringify(derivedTypes) !== JSON.stringify(this.state.selectedRulesetTypes ?? {})) {
      patch.selectedRulesetTypes = derivedTypes;
    }
    if (Object.keys(patch).length > 0) {
      this.stateChange.emit(patch);
    }
  }

  // ─── Zusammenfassung ────────────────────────────────────────────────────────

  /**
   * Vollständig ausgewählte Sets, die es auch wirklich (noch) gibt. IDs gelöschter Regelsets und
   * verwaiste IDs aus Altmanifesten bleiben im State stehen; würden sie mitgezählt, zeigt die
   * Zusammenfassung Sets an, zu denen in der Liste keine angehakte Zeile existiert
   * (z. B. „1 Set (0 Prüfungen)", obwohl nichts ausgewählt ist).
   */
  private get resolvedSelectedSets(): Ruleset[] {
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    return [...new Set(this.state.selectedRuleSetIds)]
      .map(id => all.find(r => r.id === id))
      .filter((rs): rs is Ruleset => !!rs);
  }

  /** IDs aller (bereits geladenen) Einzelregeln über alle Regelsets hinweg. */
  private get loadedRuleIds(): Set<string> {
    return new Set(Object.values(this.rulesByRulesetId).flat().map(r => r.id));
  }

  /**
   * Einzeln gewählte Regeln, die es auch wirklich (noch) gibt. IDs, die zugleich als Regelset
   * geführt werden, zählen nicht mit – bei IDS ist ruleset_id == rule_id, wodurch dieselbe
   * Auswahl sonst doppelt erscheint.
   */
  private get resolvedSelectedRuleIds(): string[] {
    const setIds = new Set(this.state.selectedRuleSetIds);
    const known = this.loadedRuleIds;
    return [...new Set(this.state.selectedRuleIds)].filter(id => !setIds.has(id) && known.has(id));
  }

  /** Anzahl ausgewählter Set-/Regel-IDs, die zu keinem vorhandenen Regelset/keiner Regel (mehr) gehören. */
  get staleSelectionCount(): number {
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const setIds = new Set(this.state.selectedRuleSetIds);
    const known = this.loadedRuleIds;
    const staleSets = [...setIds].filter(id => !all.some(rs => rs.id === id));
    const staleRules = [...new Set(this.state.selectedRuleIds)]
      .filter(id => !setIds.has(id) && !known.has(id));
    return staleSets.length + staleRules.length;
  }

  /**
   * Entfernt alle Auswahl-IDs, zu denen es kein Regelset bzw. keine Regel mehr gibt.
   * Nötig als eigene Aktion, weil verwaiste IDs keine Zeile in der Liste haben – über
   * toggleRuleset()/toggleRule() sind sie also nicht erreichbar und blieben sonst dauerhaft
   * im Manifest stehen. Bewusst nicht automatisch beim Laden: schlägt einer der Regelset-
   * Endpunkte fehl, liefert RulesetService still eine leere Liste (catchError) – dann sähe
   * eine völlig gültige Auswahl verwaist aus.
   */
  clearStaleSelection(): void {
    if (this.loading || this.rulesPreloadPending) return;
    const all = [...this.prebuiltRulesets, ...this.customRulesets];
    const known = this.loadedRuleIds;
    const setIds = [...new Set(this.state.selectedRuleSetIds)].filter(id => all.some(rs => rs.id === id));
    // IDs, die zugleich als Regelset geführt werden, gehören nicht in die Einzelregel-Auswahl.
    const ruleIds = [...new Set(this.state.selectedRuleIds)]
      .filter(id => !setIds.includes(id) && known.has(id));
    const partiallySelectedRuleSetIds = (this.state.partiallySelectedRuleSetIds ?? [])
      .filter(id => all.some(rs => rs.id === id) && this.getSetRuleIds(id).some(rid => ruleIds.includes(rid)));

    this.stateChange.emit({
      selectedRuleSetIds: setIds,
      selectedRuleIds: ruleIds,
      partiallySelectedRuleSetIds,
      selectedRulesetType: this.resolveSelectedRulesetType(setIds, ruleIds, this.state.selectedRulesetType),
    });
  }

  /** Anzahl vollständig ausgewählter Sets */
  get selectedSetCount(): number {
    return this.resolvedSelectedSets.length;
  }

  /** Summe der Regeln aller vollständig ausgewählten Sets */
  get selectedSetRuleCount(): number {
    return this.resolvedSelectedSets.reduce((sum, rs) => sum + (rs.ruleCount ?? 0), 0);
  }

  /** Anzahl einzeln ausgewählter Regeln (nur solche, die einem geladenen Set zuzuordnen sind). */
  get selectedSingleRuleCount(): number {
    return this.resolvedSelectedRuleIds.length;
  }

  /** Gesamtzahl der anzuwendenden Prüfregeln */
  get totalAppliedRuleCount(): number {
    return this.selectedSetRuleCount + this.selectedSingleRuleCount;
  }

  /** True, wenn überhaupt etwas ausgewählt wurde */
  get hasSelection(): boolean {
    return this.selectedSetCount > 0 || this.selectedSingleRuleCount > 0;
  }

  // ─── Verlinkungen in die Prüfregelverwaltung (neuer Tab) ────────────────────

  ngOnInit(): void {
    // Ohne Projekt (Admin-Modus, zentral bereitgestellte Kopie) dürfen NUR zentral bereitgestellte
    // Regelwerke auswählbar sein – ein projekt-eigenes Regelset wäre bei den Empfängern nicht
    // auflösbar. `listRulesets(undefined)` genügt dafür nicht (siehe dortiger Kommentar: liefert für
    // KI alle Projekte und für IDS/HYBRID nichts).
    const rulesets$ = this.projectId
      ? this.rulesetService.listRulesets(this.projectId)
      : this.rulesetService.listGlobalRulesetsForSelection();

    rulesets$.subscribe({
      next: (result) => {
        this.prebuiltRulesets = result.prebuilt;
        this.customRulesets = result.custom;
        this.loading = false;
        this.preloadRulesForSelection();
        this.reconcileSelectedRulesetType();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** Öffnet die Prüfregelverwaltung, um ein neues Regelset anzulegen */
  createNewRuleset(): void {
    this.openInNewTab(this.ruleManagementBase);
  }

  /** Öffnet das Regelset im Editor (neuer Tab) */
  openRuleset(ruleset: Ruleset, event: Event): void {
    event.stopPropagation();
    if (!this.canOpen(ruleset)) return;
    const typePath = BuilderRuleStepComponent.TYPE_PATH_MAP[ruleset.type] ?? 'ki';
    this.openInNewTab([...this.ruleManagementBase, ruleset.id, typePath]);
  }

  /** Öffnet eine einzelne Regel (neuer Tab) */
  openRule(ruleset: Ruleset, rule: RulesetRule, event: Event): void {
    event.stopPropagation();
    if (!this.canOpen(ruleset)) return;
    if (ruleset.type === 'HYBRID') {
      // Hybrid: eigene Route je Regel
      this.openInNewTab([...this.ruleManagementBase, ruleset.id, 'hybrid', rule.id]);
    } else if (ruleset.type === 'KI') {
      // KI: Regel wird im Set-Editor per Query-Param direkt selektiert
      this.openInNewTab([...this.ruleManagementBase, ruleset.id, 'ki'], {ruleId: rule.id});
    } else {
      // IDS: Spezifikation im übergeordneten IDS-Regelset-Editor öffnen
      this.openInNewTab([...this.ruleManagementBase, ruleset.id, 'ids']);
    }
  }

  private openInNewTab(commands: (string | number)[], queryParams?: Record<string, string>): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(commands, queryParams ? {queryParams} : {}),
    );
    window.open(url, '_blank');
  }

  selectRulesetType(type: RulesetType): void {
    this.stateChange.emit({selectedRulesetType: type});
  }

  getTypeLabel(type: string): string {
    switch (type) {
      case 'KI':
        return 'KI';
      case 'HYBRID':
        return 'Hybrid';
      case 'IDS':
        return 'IDS';
      default:
        return type;
    }
  }

  getTypeBadgeClass(type: string): string {
    switch (type) {
      case 'KI':
        return 'bg-purple-100 text-purple-700';
      case 'HYBRID':
        return 'bg-blue-100 text-blue-700';
      case 'IDS':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  }

  private isRulesetValid(ruleset: Ruleset): boolean {
    return ruleset.status === 'ACTIVE' && ruleset.ruleCount > 0;
  }

  /** IDs der (geladenen) Regeln eines Sets */
  private getSetRuleIds(rulesetId: string): string[] {
    return (this.rulesByRulesetId[rulesetId] ?? []).map(r => r.id);
  }

  private filterBySearch(rulesets: Ruleset[]): Ruleset[] {
    const validatedRulesets = rulesets.filter(r => this.isRulesetValid(r));
    const q = this.searchQuery.trim().toLowerCase();
    const filtered = q
      ? validatedRulesets.filter(r => r.name.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q))
      : validatedRulesets;
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name, 'de', {numeric: true}));
  }

  protected readonly faThumbtackAngle = faThumbtackAngle;
  protected readonly faHandPointer = faHandPointer;
}
